// The 24-hour reply window, as the inbox sees it. WhatsApp, Instagram and
// Messenger only deliver a free-form reply within 24h of the customer's last
// message. send-channel-message enforces the same rule and has the final say;
// this lets the composer explain it before the owner types.

export const REPLY_WINDOW_HOURS = 24;
const WINDOW_MS = REPLY_WINDOW_HOURS * 60 * 60 * 1000;

const WINDOWED_CHANNELS: Record<string, string> = {
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  facebook_messenger: "Messenger",
};

/** Same default as the server: a conversation without a channel is WhatsApp. */
export const normalizeChannel = (channel: string | null | undefined) =>
  (channel || "whatsapp").toLowerCase();

export const isWindowedChannel = (channel: string | null | undefined) =>
  normalizeChannel(channel) in WINDOWED_CHANNELS;

export interface ReplyWindow {
  /** False for channels without a reply window (Telegram, LINE, email). */
  applies: boolean;
  /** null while unknown (still loading, or the lookup failed). */
  open: boolean | null;
  channelLabel: string;
  lastCustomerAt: Date | null;
  closesAt: Date | null;
}

const toDate = (v: string | Date | null | undefined): Date | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The newer of two timestamps (either may be missing). */
export function latestTimestamp(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  const da = toDate(a);
  const db = toDate(b);
  if (!da) return db ? b! : null;
  if (!db) return a!;
  return da >= db ? a! : b!;
}

/** `lastCustomerAt` undefined = not known yet; null = they never messaged. */
export function replyWindowFor(
  channel: string | null | undefined,
  lastCustomerAt: string | Date | null | undefined,
  now: number,
): ReplyWindow {
  const ch = normalizeChannel(channel);
  const channelLabel = WINDOWED_CHANNELS[ch] ?? ch;
  if (!(ch in WINDOWED_CHANNELS)) {
    return { applies: false, open: true, channelLabel, lastCustomerAt: null, closesAt: null };
  }
  if (lastCustomerAt === undefined) {
    return { applies: true, open: null, channelLabel, lastCustomerAt: null, closesAt: null };
  }
  const last = toDate(lastCustomerAt);
  if (!last) {
    return { applies: true, open: false, channelLabel, lastCustomerAt: null, closesAt: null };
  }
  const closesAt = new Date(last.getTime() + WINDOW_MS);
  return { applies: true, open: now < closesAt.getTime(), channelLabel, lastCustomerAt: last, closesAt };
}

/** Plain-language reason for a message the provider reported as undelivered.
 *  `error` is ai_messages.delivery_error ({ code, title, ... } from Meta). */
export function deliveryFailureReason(error: unknown): string {
  const e = (error ?? {}) as { code?: unknown; title?: unknown };
  switch (e.code) {
    case 131047:
      return "sent more than 24 hours after their last message";
    case 131026:
      return "this number can't receive WhatsApp messages";
    case 131049:
      return "WhatsApp limited marketing messages to this person";
    case 131050:
      return "they've stopped marketing messages from you";
    case 131042:
      return "WhatsApp payment isn't set up for this number";
    case 132015:
    case 132016:
      return "Meta has paused this template";
  }
  return typeof e.title === "string" && e.title.trim() ? e.title.trim() : "WhatsApp couldn't deliver it";
}

/** An approved template the owner can send by hand: every placeholder is the
 *  customer's name or a fixed literal, and there's no media header. Mirrors
 *  isManualTemplateUsable in send-channel-message/manualTemplate.ts. */
export interface ManualTemplate {
  id: string;
  body_text: string | null;
  body_var_order: unknown;
  header_format: string | null;
  header_text: string | null;
}

export function isManualTemplateUsable(t: Omit<ManualTemplate, "id" | "body_text">): boolean {
  const format = (t.header_format ?? "none").toLowerCase();
  if (format !== "none" && format !== "text") return false;
  if (t.header_text && /\{\{/.test(t.header_text)) return false;
  const vars = Array.isArray(t.body_var_order) ? t.body_var_order : [];
  return vars.every(
    (spec) => spec === "customer_name" || (!!spec && typeof spec === "object" && "literal" in spec),
  );
}

/** The template body as the owner should read it before sending: the name
 *  slot shown as [their name], literals filled in. */
export function templatePreview(t: Pick<ManualTemplate, "body_text" | "body_var_order">): string {
  const vars = Array.isArray(t.body_var_order) ? t.body_var_order : [];
  return (t.body_text ?? "").replace(/\{\{\s*(\d+)\s*\}\}/g, (match, n: string) => {
    const spec = vars[Number(n) - 1];
    if (spec === "customer_name") return "[their name]";
    if (spec && typeof spec === "object" && "literal" in spec) {
      return String((spec as { literal: unknown }).literal ?? "");
    }
    return match;
  });
}
